import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { authApi } from '@/api/auth';
import { Field } from '@/components/ui/field';
import { ApiError } from '@/lib/api-client';
import { signupSchema, type SignupInput } from '@/lib/form-schemas';

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { organization_name: '', name: '', email: '', password: '' },
    mode: 'onSubmit',
  });

  const signup = useMutation({
    mutationFn: (input: SignupInput) =>
      authApi.signup({
        organization_name: input.organization_name,
        name: input.name?.trim() || undefined,
        email: input.email,
        password: input.password,
      }),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      const query = result.dev_verification_code
        ? `?devCode=${encodeURIComponent(result.dev_verification_code)}`
        : '';
      navigate(`/verify-email${query}`, { replace: true });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signup.mutateAsync(values);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Signup failed');
    }
  });

  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-bg p-6">
      <div className="mb-card p-10 max-w-md w-full">
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-lg bg-mb-accent-soft text-mb-accent-press flex items-center justify-center mb-4">
            <Building2 className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-xl font-bold text-mb-text">
            Create your organization
          </h1>
          <p className="text-sm text-mb-text-muted mt-1">
            Sets up your workspace and signs you in.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
          <Field
            label="Organization name"
            required
            error={form.formState.errors.organization_name?.message}
          >
            <input
              type="text"
              className="mb-input"
              placeholder="Acme Outreach"
              {...form.register('organization_name')}
            />
          </Field>
          <Field label="Your name" hint="Optional" error={form.formState.errors.name?.message}>
            <input
              type="text"
              className="mb-input"
              placeholder="Jane Doe"
              {...form.register('name')}
            />
          </Field>
          <Field label="Work email" required error={form.formState.errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              className="mb-input"
              placeholder="jane@acme.com"
              {...form.register('email')}
            />
          </Field>
          <Field
            label="Password"
            hint="At least 8 characters"
            required
            error={form.formState.errors.password?.message}
          >
            <input
              type="password"
              autoComplete="new-password"
              className="mb-input"
              {...form.register('password')}
            />
          </Field>

          {submitError ? (
            <div
              ref={errorRef}
              tabIndex={-1}
              role="alert"
              className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger focus:outline-none"
            >
              {submitError}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={signup.isPending || form.formState.isSubmitting}
            className="mb-btn-primary w-full"
          >
            {signup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create organization
          </button>
        </form>

        <p className="text-xs text-mb-text-muted text-center mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-mb-accent-press hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
